import { CloseOutlined } from '@ant-design/icons';
import { Alert } from 'antd';
import classnames from 'classnames';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import RedWarmingFill from '@/assets/images/icons/red-warming-fill.react.svg?react';
import WarmingFillMax from '@/assets/images/icons/warming-fill.max.react.svg?react';
import { useAppConfig } from '@/config/context';
import { isDji, isPro } from '@/infrastructure/auth';
import { ENV } from '@/infrastructure/env-config';
import { getParamFromUrl } from '@/infrastructure/history';
import { formatNum } from '@/infrastructure/utils/digit';
import { ResourceType } from '@/presentation/pages/resource-pack/types';
import { useStores } from '@/presentation/store';
import { ProductExceedStatus, Dimension, ResourceStatus } from '@/proto/resource-manager';
import { FreeStatus } from '@/service/pro-user';
import { ProductType } from '@/service/product-permissions';
import { resourcePackageService } from '@/service/resource-package';
import type { IGetRemindMuteRsp, IResourcePackage } from '@/service/resource-package';
import { Banner } from '../banner-pro';
import styles from './index.module.scss';
import { MuteModal } from './mute-modal';
import type { IMuteModalRef, RemindScene } from './mute-modal';

interface IProps {
  showArrearsBanner?: boolean
  setShowArrearsBanner?: React.Dispatch<React.SetStateAction<boolean>>;
  showOverdueDataBanner?: boolean
  setShowOverdueDataBanner?: React.Dispatch<React.SetStateAction<boolean>>;
  showBanner: boolean,
  setShowBanner: React.Dispatch<React.SetStateAction<boolean>>;
  showComplianceBanner: boolean,
  setShowComplianceBanner: React.Dispatch<React.SetStateAction<boolean>>;
  isShowComplianceModal: boolean,
  setIsShowComplianceModal: React.Dispatch<React.SetStateAction<boolean>>;
  liteModeRoutes: string[];
}

export const ArrearsReminder = observer((props: IProps) => {
  const { t } = useTranslation();
  const {
    showArrearsBanner,
    setShowArrearsBanner,
    showOverdueDataBanner,
    setShowOverdueDataBanner,
    showBanner,
    setShowBanner,
    showComplianceBanner,
    setShowComplianceBanner,
    isShowComplianceModal,
    setIsShowComplianceModal,
    liteModeRoutes,
  } = props;
  const { productExceed = {}, product, productType,
    rawPerm, overdueDataRecoveryBanner = {} } = useStores().ProductStore;
  const { status, exceedQuota } = productExceed;
  const { banner } = overdueDataRecoveryBanner;
  const linkParams = new URLSearchParams();
  const location = useLocation();
  const config = useAppConfig();
  const isLiteRoute = liteModeRoutes.includes(location.pathname);

  linkParams.set('appId', getParamFromUrl('productId'));

  const muteModalRef = useRef<IMuteModalRef>(null);
  const productIdForRemind = getParamFromUrl('productId') || '';

  /**
   * 静音门：GetRemindMute.mutes[0].mute_until 与本地时间对比，
   * 仅「本地时间 > mute_until」（静默已过期 / 无记录 / 查询失败 fail-open）才允许展示横幅。
   * 初始 false → 横幅等查询返回后才展示，避免静默期内的用户看到闪现。
   */
  const [muteAllowShow, setMuteAllowShow] = useState(false);
  useEffect(() => {
    const pid = productIdForRemind;
    if (!pid) {
      setMuteAllowShow(true); /* 无产品 ID 无法查询，按未静默放行 */
      return;
    }
    let cancelled = false;
    resourcePackageService.getRemindMute({ productIds: [pid] })
      .then((resp) => {
        if (cancelled) return;
        const rsp = resp as IGetRemindMuteRsp;
        const ret = rsp?.baseRsp?.retCode ?? rsp?.base_rsp?.retCode ?? 0;
        if (ret !== 0) {
          setMuteAllowShow(true); /* 查询失败：fail-open 展示（欠费类强提醒优先） */
          return;
        }
        const info = (rsp?.mutes || [])[0];
        const until = info?.muteUntil || info?.mute_until;
        if (!until) {
          setMuteAllowShow(true); /* 无静默记录 */
          return;
        }
        setMuteAllowShow(moment().isAfter(moment(until, 'YYYY-MM-DD HH:mm:ss')));
      })
      .catch(() => {
        if (!cancelled) setMuteAllowShow(true); /* 异常：fail-open */
      });
    return () => {
      cancelled = true;
    };
  }, [productIdForRemind]);

  /** 计算当前横幅属于 3 场景中哪一种（决定 Modal 文案 + 标题色） */
  const calcScene = (): RemindScene => {
    if (status === ProductExceedStatus.HAS_ALREADY_EXCEED) return 'arrears';
    if (status === ProductExceedStatus.WILL_EXCEED) return 'exceed';
    // WILL_EXPIRE 或 免费版 WillExpire（免费版到期提醒分支）— 统一 expire 场景
    return 'expire';
  };
  const remindScene: RemindScene = calcScene();

  const handleCloseSuccess = useCallback(() => {
    if (showArrearsBanner) {
      setShowArrearsBanner?.(false);
    } else {
      setShowBanner(false);
    }
  }, [showArrearsBanner, setShowArrearsBanner, setShowBanner]);

  const close = () => {
    /* 管理员才展开静音弹窗；其他角色仅本地关闭横幅（复用 handleCloseSuccess，不调接口） */

    if (rawPerm?.isAdmin) {
      muteModalRef.current?.open();
      return;
    }
    handleCloseSuccess();
  };

  useEffect(() => {
    const valid = [
      ProductExceedStatus.WILL_EXCEED,
      ProductExceedStatus.HAS_ALREADY_EXCEED,
      ProductExceedStatus.WILL_EXPIRE,
    ].includes(status);
    /* showArrearsBanner 同时驱动 Layout 的 show-banner 占位与横幅渲染：
       静音门(muteAllowShow)必须在此合并进状态，否则 Layout 留占位但内容被拦截 → 空占位 */
    setShowArrearsBanner?.(Boolean(valid && muteAllowShow));
  }, [status, muteAllowShow]);
  const [processedMessage, setProcessedMessage] = useState(banner);

  // 计算是否绑定了月活套餐（DeviceUnLimited）
  const [hasMauPackage, setHasMauPackage] = useState<boolean>(false);
  useEffect(() => {
    if (isLiteRoute) return;

    // 海外版同样沿用此逻辑，仅做展示文案的区分
    resourcePackageService.getProductResourceList({
      queryParam: {
        pageParam: {
          page: 1,
          pageSize: 100,
        },
        // 需要包含当天
        timeParam: {
          startTime: moment()
            .subtract(6, 'days')
            .startOf('day')
            .unix(),
          endTime: moment().endOf('day')
            .unix(),
        },
      },
      resourceStatus: ResourceStatus.VALID,
      dim: Dimension.PRODUCT,
    }).then((res) => {
      const list: IResourcePackage[] = res?.resources || [];
      setHasMauPackage(list.some((item: IResourcePackage) => item?.resourceType === ResourceType.DeviceUnLimited));
    })
      .catch(() => {
        setHasMauPackage(false);
      });
  }, []);

  useEffect(() => {
    if (!banner) {
      setShowOverdueDataBanner?.(false);
    } else {
      setShowOverdueDataBanner?.(true);
      // 海外版没有产品计费文档，暂时不处理
      if (banner?.includes(t('产品计费文档')) && (isPro() || isDji())) {
        setProcessedMessage(banner.replace(
          /(产品计费文档)/g,
          `<a href="${config.billingDocumentsLink}" style="color: #1890ff; text-decoration: none;" target="_blank">$1</a>`,
        ));
      } else {
        setProcessedMessage(banner);
      }
    }
  }, [banner]);

  let content: React.ReactNode = null;
  const lite = liteModeRoutes.includes(location.pathname);

  if (showArrearsBanner && !lite) {
    content = (
      <div
        className={classnames({
          [styles.arrearsReminder]: true,
          [styles.willExceed]: [ProductExceedStatus.WILL_EXCEED, ProductExceedStatus.WILL_EXPIRE].includes(status),
          [styles.hasAlreadyExceed]:
            status === ProductExceedStatus.HAS_ALREADY_EXCEED,
        })}
      >
        {status === ProductExceedStatus.WILL_EXCEED && !ENV.isGlobal ? (
          <span className={styles.txt}>
            <WarmingFillMax />
            {t('尊敬的用户，当前产品资源包剩余上报量少于{{period}}用量，请及时', { period: hasMauPackage ? t('最近两个月') : t('最近15天') })}
            <Link
              to={{ pathname: '/resource-pack', search: linkParams.toString() }}
              target="_blank"
            >
              {t('购买资源包')}
            </Link>
            {t('，以免影响数据上报、告警通知等服务。')}
          </span>
        ) : null}
        {status === ProductExceedStatus.WILL_EXPIRE && !ENV.isGlobal ? (
          <span className={styles.txt}>
            <WarmingFillMax />
            {t('尊敬的用户，当前产品套餐即将过期，请及时')}
            <Link
              to={{ pathname: '/resource-pack', search: linkParams.toString() }}
              target="_blank"
            >
              {t('购买资源包')}
            </Link>
            {t('，以免影响数据上报、告警通知等服务。')}
          </span>
        ) : null}
        {status === ProductExceedStatus.WILL_EXPIRE && ENV.isGlobal ? (
          <span className={styles.txt}>
            <WarmingFillMax />
            {t('尊敬的用户，当前产品套餐即将过期。')}
          </span>
        ) : null}
        {status === ProductExceedStatus.WILL_EXCEED && ENV.isGlobal ? (
          <span className={styles.txt}>
            <WarmingFillMax />
            {t('尊敬的用户，当前产品资源包剩余上报量少于{{period}}用量。', { period: hasMauPackage ? t('最近两个月') : t('最近15天') })}
          </span>
        ) : null}

        {status === ProductExceedStatus.HAS_ALREADY_EXCEED && !ENV.isGlobal ? (
          <span className={styles.txt}>
            <RedWarmingFill />
            {t('尊敬的用户，当前已经欠费（')}{formatNum((exceedQuota || 0) as number)}
            {t('条上报量未正常抵扣），平台已经停止数据上报及告警等服务，请及时')}
            <Link
              to={{ pathname: '/resource-pack', search: linkParams.toString() }}
              target="_blank"
            >
              {t('购买资源包')}
            </Link>
            {t('，恢复相关服务。')}
          </span>
        ) : null}
        {status === ProductExceedStatus.HAS_ALREADY_EXCEED && ENV.isGlobal ? (
          <span className={styles.txt}>
            <RedWarmingFill />
            {t('尊敬的用户，当前已经欠费（')}{formatNum((exceedQuota || 0) as number)}
            {t('条上报量未正常抵扣），平台已经停止数据上报及告警等服务。')}
          </span>
        ) : null}
        <CloseOutlined
          onClick={close}
          title={t('关闭')}
          className={styles.closeIcon}
        />
      </div>
    );
  } else if (showOverdueDataBanner && !lite) {
    content = (
      <Alert
        message={<span dangerouslySetInnerHTML={{ __html: processedMessage }} />}
        type="success"
        showIcon
        className={styles.overdueDataRecoveryReminder}
      />
    );
  } else if (productType === ProductType.FREE_PRODUCT
    && product
    && product.freeStatus === FreeStatus.WillExpire
    && !lite
    && muteAllowShow
  ) {
    const expireAtFormat = moment(product.expireDate).format(t('YYYY年MM月DD日'));

    setShowBanner(true);
    content = (
      <div
        className={classnames({
          [styles.arrearsReminder]: true,
          [styles.willExceed]: true,
        })}
      >
        <span className={styles.txt}>
          <WarmingFillMax />
          {t('当前产品为体验产品，有效期至{{date}}。到期后，您将无法在专业版中查看该产品数据。为避免影响您的正常使用，请您及时', { date: expireAtFormat })}
          <Link
            to={{ pathname: '/resource-pack', search: linkParams.toString() }}
            target="_blank"
          >{t('升级为付费产品。')}</Link>
          <CloseOutlined
            onClick={close}
            title={t('关闭')}
            className={styles.closeIcon}
          />
        </span>
      </div>
    );
  } else {
    content = (
      <Banner
        showComplianceBanner={showComplianceBanner}// 控制合规banner
        setShowComplianceBanner={setShowComplianceBanner}
        showBanner={showBanner}
        setShowBanner={setShowBanner}
        isShowComplianceModal={isShowComplianceModal}// 控制合规弹窗
        setIsShowComplianceModal={setIsShowComplianceModal}
      />
    );
  }

  return (
    <>
      {content}
      <MuteModal
        ref={muteModalRef}
        productId={productIdForRemind}
        remindScene={remindScene}
        onCloseSuccess={handleCloseSuccess}
      />
    </>
  );
});
